import { useAtom } from "jotai";
import React, { Suspense, useEffect, useRef, useState } from "react";
import withLayout from "../../../src/layouts/withLayout";
import { Specification } from "@Api";
import { Spin } from "antd";
import styled from "styled-components";
import Filter, { IParams } from "@Components/Specification/filter";
import Crumbs from "@Components/Specification/crumbs";
import RightFixedBar from "@Components/Specification/rightFixedBar";

import {
  EItemsDiffType,
  IDataItem,
  IFilterParams,
  ILeftList,
  ISearchModel,
  ISpecificationParams,
  ITransformedData,
} from "@Types/specification";
import { useDebounceFn, useThrottleEffect, useThrottleFn } from "ahooks";
import SearchModel from "@Components/Specification/searchModel";
import TableSpecification from "@Components/Specification/table";
import { host } from "@Services/url";
import { setUrlFormat } from "@Lib/common";
import { pkComparisonAtom } from "@Lib/jotai/pkCompare";
import { g4 } from "../../utils/helper";

const AddToPK = React.lazy(() => import("@Components/Specification/addToPK"));

const { www } = host;

const { fetchSpecification } = Specification;

const SpinStyled = styled(Spin)`
  .ant-spin-dot-item {
    background-color: #3264ff;
  }
  .ant-spin-text {
    margin-top: 10px;
  }
`;

const App: React.FC = () => {
  const searchRef = useRef({});
  const [fetchData, setFetchData] = useState<IDataItem[]>([]); // 接口数据结构
  const [transformedData, setTransformedData] = useState<ITransformedData>({ modelYearInfo: [], equipSpec: [] }); // 重组可还原的数据
  const [filteredTransformedData, setFilteredTransformedData] = useState<ITransformedData>({
    modelYearInfo: [],
    equipSpec: [],
  }); // 过滤操作后的数据
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchData, setSearchData] = useState<ISearchModel[]>([]);
  const [leftList, setLeftList] = useState<ILeftList[]>([]);
  const [openPK, setOpenPK] = useState(false);
  const [params, setParams] = useState<ISpecificationParams>({ kindId: "", nk: "", inStock: "" });
  const [filterParams, setFilterParams] = useState<IFilterParams>({ driveType: [], power: [], gas: [] });
  const [saveFilterParams, setSaveSaveFilterParams] = useState<IFilterParams>({
    driveType: [],
    power: [],
    gas: [],
    diffItems: [EItemsDiffType.HIDE_NONE],
  });
  const [sectionChange, setSectionChange] = useState<number>(0);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const isFirstModelRef = useRef({ firstId: null, isFirstGetId: true });
  const [pkDataCount, setPkDataCount] = useState(0);
  const [globalPkList] = useAtom(pkComparisonAtom);
  const [isPKQuestion, setIsPKQuestion] = useState(true);


  const getData = async () => {
    try {
      const res: any = await fetchSpecification({
        ...params,
        firstModelYearId: isFirstModelRef.current.isFirstGetId ? isFirstModelRef.current.firstId : "",
      });

      if (res && res.status === 12000) {
        setSaveSaveFilterParams({ driveType: [], power: [], gas: [], diffItems: [EItemsDiffType.HIDE_NONE] });
        setFetchData(res.data.items);
        const { autoType, brands, kinds, priceRange } = res?.data?.remarketing || {};

        g4("remarketing", {
          dv_price_range: priceRange,
          dv_auto_type: autoType,
          dv_kind: kinds,
          dv_brand: brands,
          dv_page: "規格配備",
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  // 重组数据
  const transformData: any = (data: IDataItem[]) => {
    const modelYearInfos = data.map((entry: any) => ({
      ...entry.modelYearInfo,
      name: `${entry.modelYearInfo.kindName} ${!!entry.modelYearInfo.year ? entry.modelYearInfo.year + "年式" : ""} ${entry.modelYearInfo.modelName
        }`,
      price: !!entry.modelYearInfo.price ? `${entry.modelYearInfo.price}萬` : "暫無報價",
    }));

    const equipSpecs: any[] = data.map((entry) => {
      // 第一层车款对应列
      return entry.equipSpec.map((spec) => {
        // 第二层对应基础行
        const newItems = spec.items.map((item) => {
          // itemss第三层对应实际values
          const newItem = { ...item, itemss: [] };

          data.forEach((innerEntry) => {
            const modelYearId = innerEntry?.modelYearInfo?.modelYearId;
            if (!modelYearId) return; // 若没有 modelYearId，跳过

            innerEntry.equipSpec.forEach((innerSpec) => {
              innerSpec.items.forEach((innerItem) => {
                if (innerItem.name === item.name && !newItem.itemss?.find((i) => i.modelYearId === modelYearId)) {
                  newItem.itemss.push({
                    type: innerItem.type,
                    unit: innerItem.unit,
                    value: innerItem.value,
                    modelYearId: modelYearId,
                  });
                }
              });
            });
          });
          return newItem;
        });

        return {
          ...spec,
          items: newItems,
        };
      });
    });

    // 数据扁平化
    const mergedEquipSpecs = !!equipSpecs.length ? [].concat(...equipSpecs[0]) : [];
    return { modelYearInfo: modelYearInfos, equipSpec: mergedEquipSpecs };
  };

  // 关闭PK比较抽屉
  const onClose = () => {
    setOpenPK(false);
  };

  // 搜索
  useThrottleEffect(
    () => {
      if (!!searchValue && !!filteredTransformedData.equipSpec.length) {
        const newData: any[] = filteredTransformedData.equipSpec
          .map((item) => {
            const filteredItems = item.items
              .filter((i) => i.name.includes(searchValue))
              .map((i) => ({ name: i.name, entryId: i.entryId }));
            return filteredItems.length > 0 ? filteredItems : false;
          })
          .filter(Boolean);

        const flattenedArray = newData.reduce((acc, curr) => acc.concat(curr), []);

        setSearchData([...flattenedArray]);
      }
    },
    [searchValue],
    { wait: 1000 },
  );

  // 年式更新参数
  const updateParams = (params: IParams) => {
    setParams({
      kindId: window.query_kind_id,
      nk: !!params.inStock ? (params.inStock === "1" ? params.isSalesCheckbox.join(",") : params.stopSale) : "",
      inStock: params.inStock || "",
    });
  };

  // 更新去重后的排氣量、動力、驅動
  const uniqueData = (key: string) => {
    return fetchData
      .filter(
        (value, index, self) => index === self.findIndex((t) => t.modelYearInfo[key] === value.modelYearInfo[key]),
      )
      .filter((item) => !!item.modelYearInfo[key])
      .map((item) => item.modelYearInfo[key]);
  };

  // 排氣量、動力、驅動、高亮不同、隐藏相同、隐藏暂无 筛选后过滤总数据
  const updateFilterCheckbox = (params: {
    gas: string[];
    power: string[];
    driveType: string[];
    diffItems?: string[];
  }) => {
    setSaveSaveFilterParams(params);

    const newFormedData = JSON.parse(JSON.stringify(transformedData));
    let newDataModelYearInfo: any[] = [];

    newDataModelYearInfo = newFormedData.modelYearInfo.filter((item) => {
      let matchGas = !!params.gas.length ? params.gas.some((gas) => item.gas === gas) : true;
      let matchPower = !!params.power.length ? params.power.some((power) => item.power === power) : true;
      let matchDriveType = !!params.driveType.length
        ? params.driveType.some((driveType) => item.driveType === driveType)
        : true;

      return matchGas && matchPower && matchDriveType;
    });

    const newDataModelYearInfoIds = newDataModelYearInfo.map((item) => item.modelYearId);

    // 排氣量、動力、驅動
    const newEquipSpec = newFormedData.equipSpec.map((item) => {
      item.items = item.items.map((i) => {
        const seenIds = new Set();
        i.itemss = i.itemss.filter((ii) => {
          if (newDataModelYearInfoIds.includes(ii.modelYearId) && !seenIds.has(ii.modelYearId)) {
            seenIds.add(ii.modelYearId);
            return true;
          }
          return false;
        });
        return i;
      });
      return item;
    });

    // 只剩一条车型数据，高亮不同、隐藏相同 置灰并清除选中
    if (!!newDataModelYearInfo.length && newDataModelYearInfo.length === 1) {
      params.diffItems = params.diffItems!.includes(EItemsDiffType.HIDE_NONE) ? [EItemsDiffType.HIDE_NONE] : [];
    }

    // 高亮不同、隐藏相同、隐藏暂无
    const result =
      !!(newEquipSpec || []).length &&
      !!(params.diffItems || []).length &&
      newEquipSpec.map((spec) => ({
        ...spec,
        items: spec.items
          .map((item) => {
            const seenValues = new Set();
            let filteredItemss = item.itemss;

            filteredItemss = filteredItemss.filter((entry) => {
              if (!seenValues.has(entry.value)) {
                seenValues.add(entry.value);
                return true;
              }
              return false;
            });

            // 隱藏相同項
            if (params.diffItems!.includes("HideIdentical") && filteredItemss.length === 1) return null;

            // 隱藏暫無項
            if (params.diffItems!.includes("HideNone") && filteredItemss.length === 1 && filteredItemss[0].value === "")
              return null;

            return {
              ...item,
              itemss: item.itemss,
            };
          })
          .filter((item) => item !== null),
      }));

    setFilteredTransformedData({
      modelYearInfo: newDataModelYearInfo,
      equipSpec: !!(params.diffItems || []).length ? result : newEquipSpec,
    });
  };

  // 搜索点击
  const handleClickSearch = (entryId: number) => {
    if (searchRef.current) {
      (searchRef.current as any).scrollToItem(entryId);
    }
  };

  // 左侧栏点击
  const debouncedLeftClick = (params) => {
    setSectionChange(params);
  };

  // 监听在售、停售获取数据
  const { run: debouncedGetData } = useDebounceFn(
    () => {
      if (isFirstModelRef.current.firstId === null) {
        isFirstModelRef.current.firstId = window.FIRST_MODEL_YEAR_ID;
        isFirstModelRef.current.isFirstGetId = true;
      } else if (!!isFirstModelRef.current.firstId) {
        isFirstModelRef.current.isFirstGetId = false;
      }

      getData();
    },
    { wait: 600 },
  );

  // 滚动监听函数
  const { run: debouncedHandleScroll } = useThrottleFn(
    () => {
      const scrollTop = window.scrollY;
      if (isSticky !== scrollTop > 0) {
        setIsSticky(scrollTop > 0);
      }
      if (scrollTop === 0 && leftList.length > 0) {
        setSectionChange(leftList[0].id);
      }
    },
    { wait: 30 },
  );

  useEffect(() => {
    // baseLayout 中 dimensions 会重复赋值，导致规格页中无法获取到 dimensions
    window.dimensions = null;
    const handleScroll = () => debouncedHandleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    debouncedGetData();

    return () => debouncedGetData.cancel();
  }, [params]);

  // 获取接口数据格式
  useEffect(() => {
    if (fetchData.length > 0) {
      const $container: any = document.getElementById("container");
      $container.style.display = "grid";

      if (!!window.FIRST_MODEL_YEAR_ID) {
        const newFetchData = JSON.parse(JSON.stringify(fetchData));
        const firstModelIndex = newFetchData.findIndex(
          (item) => item.modelYearInfo.modelYearId == window.FIRST_MODEL_YEAR_ID,
        );
        if (firstModelIndex !== -1) {
          const firstModel = newFetchData[firstModelIndex];
          newFetchData.splice(firstModelIndex, 1);
          newFetchData.unshift(firstModel);
        }
        setTransformedData(transformData(newFetchData));
      } else {
        setTransformedData(transformData(fetchData));
      }

      setFilterParams({
        driveType: uniqueData("driveType"),
        power: uniqueData("power"),
        gas: uniqueData("gas"),
      });
    }
  }, [fetchData]);

  // 监听重组后数据格式，可还原(删除、固钉)
  useEffect(() => {
    if (transformedData.equipSpec.length > 0) {
      updateFilterCheckbox({ ...saveFilterParams });
    }
  }, [transformedData]);

  // 监听过滤后数据格式
  useEffect(() => {
    if (filteredTransformedData.equipSpec.length > 0) {
      const newLeftList = filteredTransformedData.equipSpec
        .map((item) => {
          return {
            id: item.id,
            name: item.name,
            items: item.items,
          };
        })
        .filter((item) => item.items.length > 0);

      setLeftList(newLeftList);

      debouncedLeftClick(filteredTransformedData.equipSpec[0].id);
    }
  }, [filteredTransformedData]);

  // 监听PK比较数据
  useEffect(() => {
    setPkDataCount(globalPkList.length || 0);
  }, [globalPkList]);

  return (
    <div className={`min-h-screen bg-[#F8F9FC] flex rounded-tl-4 pt-8 sticky z-10 ${isSticky ? "z-[1000]" : ""}`}>
      {/* 左侧栏 */}
      <div className='w-180 min-w-[180px] max-w-[180px] sticky left-0 bg-[#F8F9FC] pl-28 pr-18 z-[9]'>
        <div className='sticky top-0 pt-8 left-0 '>
          <div className='w-134 flex items-center justify-between rounded-4 border-1 border-solid border-[#9DA2AF] mb-12 cursor-pointer text-14 relative'>
            <input
              id='SearchDom'
              type='text'
              className='w-full h-32 rounded-4 placeholder:text-[#9DA2AF] px-12 text-[#1D2029]'
              placeholder='搜尋規格配備項'
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <SearchModel
              sourceData={searchData}
              searchValue={searchValue}
              handleClick={handleClickSearch}
              onClear={() => setSearchValue("")}
            />
          </div>

          <div id='tracking-leftList' className='w-134 flex flex-col items-start justify-between'>
            {!!leftList.length &&
              leftList.map((item) => (
                <span
                  className={`tracking-leftListItem ${Number(sectionChange) === item.id ? "bg-[#3264FF] text-white" : "text-[#1D2029] hover:bg-[#E0EFFF]"
                    } text-14 w-full h-32 rounded-4 pl-12 flex items-center  cursor-pointer `}
                  key={item.id}
                  onClick={() => {
                    setTimeout(() => {
                      debouncedLeftClick(item.id);
                    }, 300);
                    (searchRef.current as any).clickLeftItem(item.id);
                  }}
                >
                  {item.name}
                </span>
              ))}
          </div>
        </div>
      </div>

      {/* 右主内容 */}
      <div className='flex flex-col items-start justify-start flex-1 bg-[#fff]'>
        {/* 面包屑 */}
        <div className='h-42 bg-[#F8F9FC] w-full'>
          <Crumbs
            sticky={true}
            stickyLeft={180}
            items={[
              { label: "首頁", url: www },
              { label: "找車", url: "/Models" },
              { label: window.BRAND_EN_NAME, url: `/Models/${setUrlFormat(window.BRAND_EN_NAME)}` },
              {
                label: window.KIND_NAME,
                url: `/${setUrlFormat(window.BRAND_EN_NAME)}/${setUrlFormat(window.KIND_NAME)}/Summary.html`,
              },
              { label: "規格配備" },
            ]}
          />
        </div>
        <section className='bg-[#fff]'>
          {/* 筛选 */}
          <Filter
            updateParams={updateParams}
            filterParams={filterParams}
            updateFilterCheckbox={(e) => updateFilterCheckbox({ ...e, diffItems: saveFilterParams.diffItems })}
          />
          {/* 加入PK比较抽屉 */}
          <Suspense fallback={null}>
            <AddToPK open={openPK} onClose={onClose} />
          </Suspense>
          {/* 右侧置顶固定栏 */}
          <RightFixedBar
            items={[
              {
                defaultSrc: "https://photo.8891.com.tw/2024/11/11/1731308340899356.png",
                hoverSrc: "https://photo.8891.com.tw/2024/11/11/1731305235679649.png",
                label: (
                  <div className='relative'>
                    {!!pkDataCount && (
                      <i className='bg-[#FF2101] rounded-full flex items-center justify-center absolute -top-34 left-16 text-14 text-white min-w-[16px] h-16 w-fit'>
                        {pkDataCount > 99 ? "99+" : pkDataCount}
                      </i>
                    )}
                    比較
                  </div>
                ),
                id: "tracking-RightPKBtn",
                itemIndex: 0,
                handleClick: () => {
                  setOpenPK(true);
                },
              },
            ]}
            scrollTopNum={176}
          />

          {!!filteredTransformedData.equipSpec.length ? (
            <>
              <TableSpecification
                fetchData={fetchData}
                filteredTransformedData={filteredTransformedData}
                transformedData={transformedData}
                searchRef={searchRef}
                isFirstModelRef={isFirstModelRef}
                setTransformedData={setTransformedData}
                setFilteredTransformedData={setFilteredTransformedData}
                setOpenPK={setOpenPK}
                onSectionChange={(title) => debouncedLeftClick(title)}
                updateFilterCheckbox={(params) => updateFilterCheckbox({ ...saveFilterParams, diffItems: [...params] })}
              />

              <p className='mt-14 mb-20 text-[#656873] pl-14 sticky left-[180px] w-300'>
                溫馨提示：以上數據僅供參考，車型參配以實車為準
              </p>
            </>
          ) : (
            <SpinStyled
              size='large'
              className='w-[1200px] h-full mt-[300px]'
              tip={<span className='text-14 text-[#3264FF] pt-20'>載入中...</span>}
            />
          )}
        </section>
      </div>
    </div>
  );
};

withLayout({
  cmp: <App />,
  params: {
    singlePageNav: true,
    webPageNav: false,
    nav: "",
    footer: "Footer",
  },
});
